import cx from 'classnames';
import PropTypes from 'prop-types';
import { Button, ArrowLink } from '@bwoty-web/ui-kit';
import { useIsGlobetrotter, useIsMobile } from '@bwoty-web/ui-kit/hooks';

import { urlUtils } from '../../../utils/url';

import './_pricePanel.scss';

const PricePanel = ({
  addToCart = null,
  displaySettings,
  flightInformation = {},
  hideBookButton = false,
  labels,
  openDetails = () => {},
  searchInformation,
}) => {
  const {
    cabinClass,
    homeBoundFlights,
    id: flightId,
    isAddingToCart,
    isBookBtnDisabled,
    luggageIncluded,
    navigationUrl,
    outBoundFlights,
    priceBreakDown,
  } = flightInformation;
  const isGlobe = useIsGlobetrotter();
  const isMobile = useIsMobile();
  const { isDiscounted, totalPriceBeforeDiscount, totalPrice } = priceBreakDown || {};
  const { isSummaryView, hidePriceDetails } = displaySettings || {};

  const firstOutBoundFlight = outBoundFlights[0];
  const firstHomeBoundFlight = homeBoundFlights[0];

  const outboundTransportKey = firstOutBoundFlight.bookingEngineKey;
  const homeboundTransportKey = firstHomeBoundFlight.bookingEngineKey;
  const cabinClassString = cabinClass ? `, ${cabinClass}` : '';

  const passengerInfo = searchInformation?.passengerInfo;
  const luggage = luggageIncluded ? labels.luggageIncluded : labels.luggageNotIncluded;
  const isReadyForPassengerDetails =
    (!!urlUtils.getRoomGroupKeyFromUrl() || urlUtils.isFlightOnly()) && !navigationUrl.includes('#price-list');

  const handleNavigation = () => {
    if (isReadyForPassengerDetails) {
      addToCart(homeboundTransportKey, outboundTransportKey, navigationUrl, flightId);
    } else {
      window.location.href = urlUtils.standaloneUrlHandler(navigationUrl);
    }
  };

  const getButtonLabel = () => {
    if (isReadyForPassengerDetails) return labels.bookBtnLabel;
    if (isSummaryView) return labels.changeTransport;
    return labels.selectBtnLabel;
  };

  const renderPriceDetails = () =>
    priceBreakDown &&
    !isSummaryView && (
      <div className="iw-flight-list-price-panel__price-container">
        {isDiscounted && <span className="iw-flight-list-price-panel__original-price">{totalPriceBeforeDiscount}</span>}
        <span
          className={cx('iw-flight-list-price-panel__current-price', {
            'iw-flight-list-price-panel__current-price--discounted': isDiscounted,
          })}
        >
          {totalPrice}
        </span>
      </div>
    );

  const renderPriceDetailsLink = (priceDetailsLinkClass) => (
    <ArrowLink
      aria-haspopup="dialog"
      className={priceDetailsLinkClass}
      onClick={() => {
        const index = hidePriceDetails ? 0 : 3;
        openDetails(index);
      }}
    >
      {hidePriceDetails ? labels.flightInfoDetailsHeading : labels.priceDetailsHeading}
    </ArrowLink>
  );

  const renderPricePanelContent = () => {
    if (isSummaryView) {
      return <p className="iw-flight-list-price-panel__change-flight-label">{labels?.changeFlightOptions}</p>;
    }
    if (isGlobe) {
      return renderPriceDetailsLink('iw-flight-list-price-panel__details-link-desktop');
    }
    return null;
  };

  return (
    <div
      className={cx('iw-flight-list-price-panel', {
        'iw-flight-list-price-panel--summary': isSummaryView,
      })}
    >
      {!isSummaryView && (
        <div className="iw-flight-list-price-panel__mobile-left">
          {passengerInfo && <span>{`${labels.productsIncludedParsed}, ${passengerInfo}`}</span>}
          <span>{`${luggage}${cabinClassString}`}</span>
          {renderPriceDetailsLink('iw-flight-list-price-panel__details-link-mobile')}
        </div>
      )}

      {!isSummaryView && (
        <div className="iw-flight-list-price-panel__mobile-middle">
          {isGlobe && renderPriceDetails()}
          {/* Not implemented yet */}
          {/* {!isGlobe && !isMobile && (
            <div className="iw-flight-list-price-panel__tags-container">
              <Tag>Tag Text</Tag>
              <Tag>Tag Text</Tag>
            </div>
          )} */}
          {!isMobile && (
            <div>
              {passengerInfo && (
                <span className="iw-flight-list-price-panel__pax-container">
                  {`${labels.productsIncludedParsed}, ${passengerInfo}`}
                </span>
              )}
              <span className="iw-flight-list-price-panel__desktop-luggage-cabincals">
                {`${luggage}${cabinClassString}`}
              </span>
            </div>
          )}
          {!isGlobe && renderPriceDetailsLink('iw-flight-list-price-panel__details-link-desktop')}
        </div>
      )}

      <div className="iw-flight-list-price-panel__book-btn-container">
        {!isGlobe && renderPriceDetails()}
        {!hideBookButton && (
          <Button
            size="s"
            variant={isSummaryView ? 'secondary' : 'primary'}
            loading={isAddingToCart}
            disabled={isBookBtnDisabled}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              handleNavigation();
            }}
            className="iw-flight-list-price-panel__book-btn"
          >
            {getButtonLabel()}
          </Button>
        )}
        {renderPricePanelContent()}
      </div>
    </div>
  );
};

PricePanel.propTypes = {
  flightInformation: PropTypes.shape({
    cabinClass: PropTypes.string,
    isAddingToCart: PropTypes.bool,
    isBookBtnDisabled: PropTypes.bool,
    luggageIncluded: PropTypes.bool,
    priceBreakDown: PropTypes.shape({}),
    id: PropTypes.string,
    outBoundFlights: PropTypes.arrayOf(
      PropTypes.shape({
        bookingEngineKey: PropTypes.string,
      }),
    ),
    homeBoundFlights: PropTypes.arrayOf(
      PropTypes.shape({
        bookingEngineKey: PropTypes.string,
      }),
    ),
    navigationUrl: PropTypes.string,
  }),
  searchInformation: PropTypes.shape({
    passengerInfo: PropTypes.string,
  }).isRequired,
  labels: PropTypes.shape({
    bookBtnLabel: PropTypes.string,
    selectBtnLabel: PropTypes.string,
    priceDetailsHeading: PropTypes.string,
    luggageIncluded: PropTypes.string,
    luggageNotIncluded: PropTypes.string,
    changeTransport: PropTypes.string,
    flightInfoDetailsHeading: PropTypes.string,
    productsIncludedParsed: PropTypes.string,
    changeFlightOptions: PropTypes.string,
  }).isRequired,
  openDetails: PropTypes.func,
  addToCart: PropTypes.func,
  displaySettings: PropTypes.shape({
    isSummaryView: PropTypes.bool,
    hidePriceDetails: PropTypes.bool,
  }).isRequired,
  hideBookButton: PropTypes.bool,
};

export default PricePanel;
